import React, { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import BLUAgent from "./BLUAgent";
import {
  BLU_OPEN_EVENT,
  getBluBubbleVariant,
  setBluBubbleVariant,
} from "../blu/bluBubbleVariant";

const SHELL = {
  compact: {
    width: "min(92vw, 400px)",
    height: "min(70vh, 540px)",
  },
  split: {
    width: "min(96vw, 560px)",
    height: "min(74vh, 580px)",
  },
  stack: {
    width: "min(92vw, 420px)",
    height: "min(72vh, 560px)",
  },
};

export function BLUFloatingChat() {
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState(() => getBluBubbleVariant());
  const [launchSeed, setLaunchSeed] = useState(null);

  useEffect(() => {
    const onOpen = (e) => {
      const next = e?.detail?.variant;
      if (next) {
        setBluBubbleVariant(next);
        setVariant(next);
      } else {
        setVariant(getBluBubbleVariant());
      }
      setLaunchSeed(e?.detail?.seed || null);
      setOpen(true);
    };
    window.addEventListener(BLU_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(BLU_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const shell = SHELL[variant] || SHELL.split;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setVariant(getBluBubbleVariant());
          setOpen(true);
        }}
        className="blu-fab fixed z-[80] h-12 w-12 rounded-full text-[var(--t-text)] shadow-lg transition-colors hover:text-[var(--t-accent)]"
        aria-label="Open BLU assistant"
        data-testid="blu-fab"
      >
        <Sparkles size={18} strokeWidth={1.4} className="mx-auto" />
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[90] bg-black/25"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="BLU assistant"
          data-testid="blu-overlay"
        >
          <div
            className="neo-panel neo-drawer blu-popover fixed z-[95] flex flex-col overflow-hidden rounded-2xl"
            style={{
              right: 20,
              width: shell.width,
              height: shell.height,
              maxHeight: shell.height,
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="blu-popover"
            data-blu-variant={variant}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--t-border)]/40 px-4 py-3">
              <div className="min-w-0">
                <div className="label-eyebrow mb-0.5">BLU</div>
                <div className="font-heading text-base tracking-tight !font-light">Studio agent</div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="shrink-0 rounded-xl p-2 text-[var(--t-meta)] hover:bg-black/[0.04] hover:text-[var(--t-text)]"
                aria-label="Close"
                data-testid="blu-close"
              >
                <X size={16} strokeWidth={1.4} />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <BLUAgent
                embedded
                bubble={variant}
                launchSeed={launchSeed}
                onLaunchSeedConsumed={() => setLaunchSeed(null)}
              />
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .blu-fab {
          animation: blu-float 3.4s ease-in-out infinite;
          background: rgba(0, 30, 255, 0.03);
          border: 1px solid rgba(0, 30, 255, 0.08);
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
          right: 20px;
          bottom: 20px;
        }
        .blu-popover {
          bottom: 84px;
        }
        @media (max-width: 767px) {
          .blu-fab { bottom: 92px; }
          .blu-popover { bottom: 156px; }
        }
        @keyframes blu-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .blu-fab { animation: none; }
        }
      `}</style>
    </>
  );
}
